import { Plus, Trash2 } from "lucide-react";
import React from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Menu,
  Space,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import Survey from "../services/model/Survey";
import DeleteSurveyQuestionButton from "../elements/DeleteSurveyQuestionButton";
import DeleteSurveyQuestionOptionButton from "../elements/DeleteSurveyQuestionOptionButton";
import { SurveyFormInterface } from "./SurveyForm";

type SurveyQuestionFormProps = {
  form: SurveyFormInterface;
  values?: Survey;
};

const SurveyQuestionForm: React.FC<SurveyQuestionFormProps> = ({
  form,
  values,
}) => {
  const maxOptions = 8;
  const maxOptionCharacters = 24;
  const maxQuestionCharacters = 255;

  const onDeleteQuestionHandler = (questionNumber: number): void => {
    form.removeListItem("questions", questionNumber);
  };

  const onQuestionOptionChangeHandler = (
    questionNumber: number,
    choiceNumber: number,
    content: string
  ): void => {
    const options = form.values.questions[questionNumber].choices;
    options[choiceNumber] = {
      ...form.values.questions[questionNumber].choices[choiceNumber],
      content,
    };
    form.setListItem(`questions`, questionNumber, {
      ...form.values.questions[questionNumber],
      choices: options,
    });
  };

  const onDeleteQuestionOptionHandler = (
    questionNumber: number,
    choiceNumber: number
  ): void => {
    const questions = form.values.questions[questionNumber];
    const isFirstIndex =
      questions.choices.indexOf(questions.choices[choiceNumber]) === 0;
    questions.choices.splice(
      choiceNumber,
      questions.choices.indexOf(questions.choices[choiceNumber]) +
        (isFirstIndex ? 1 : 0)
    );
    form.setListItem(`questions`, questionNumber, {
      ...questions,
    });
  };

  const onAddQuestionOptionHandler = (
    questionNumber: number,
    content: string
  ): void => {
    form.setListItem(`questions`, questionNumber, {
      ...form.values.questions[questionNumber],
      choices: [
        ...form.values.questions[questionNumber].choices,
        {
          id: undefined,
          content,
        },
      ],
    });
  };

  const onAddQuestionHandler = (type: string): void => {
    form.addListItem("questions", {
      id: undefined,
      question_type: type,
      content: "",
      choices: [],
    });
  };

  return (
    <>
      {form.values.questions.map((_, index: number) => (
        <>
          <div
            className="Question-Cards"
            key={
              //  eslint-disable-next-line react/no-array-index-key
              `questions-${index}`
            }
          >
            <div className="question-card-header">
              <div className="question-card-chip-wrapper">
                <Badge className="question-number-indicator">
                  {index + 1}/{form.values.questions.length}
                </Badge>
                <Text className="question-type-label">
                  {Object.is(
                    form.values.questions[index].question_type,
                    "free-text"
                  )
                    ? "Open-ended"
                    : "Multiple choice"}
                </Text>
              </div>
              {form.values.questions[index]?.id && values?.id ? (
                <DeleteSurveyQuestionButton
                  id={
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    form.values.questions[index]?.id || Math.random()
                  }
                  surveyId={values.id}
                  onDone={() => onDeleteQuestionHandler(index)}
                />
              ) : (
                <Menu trigger="hover">
                  <Menu.Item
                    onClick={() => onDeleteQuestionHandler(index)}
                    color="red"
                    icon={<Trash2 size={14} />}
                  >
                    Delete question
                  </Menu.Item>
                </Menu>
              )}
            </div>
            <div>
              <Textarea
                styles={{
                  defaultVariant: {
                    width: "100%",
                    height: "31px",
                    border: 0,
                    borderBottom: "1px solid #062133",
                    borderRadius: "0 !important",
                  },
                }}
                maxLength={maxQuestionCharacters}
                required
                {...form.getListInputProps("questions", index, "content")}
                placeholder="Enter question"
              />
              <div className="question-word-count">
                <span className="question-word-count-label">
                  {maxQuestionCharacters -
                    form.values.questions[index].content.length}{" "}
                  characters left
                </span>
              </div>
              <Space h="xl" />
              {Object.is(form.values.questions[index].question_type, "mcq") && (
                <>
                  <Stack spacing="xl">
                    {form.values.questions[index].choices.map(
                      (_g, optionIndex: number) => (
                        <>
                          <div
                            key={
                              // eslint-disable-next-line react/no-array-index-key
                              `questions[${index}].choices[${optionIndex}].content`
                            }
                            className="question-options-container"
                          >
                            <div className="question-options-bullet" />
                            <div className="ml-3 grow">
                              <Textarea
                                placeholder={`Option ${optionIndex + 1}`}
                                variant="unstyled"
                                autoFocus
                                required
                                maxLength={maxOptionCharacters}
                                style={{
                                  paddingTop: "24px",
                                  paddingBottom: 0,
                                }}
                                value={
                                  form.values.questions[index].choices[
                                    optionIndex
                                  ].content
                                }
                                onChange={({ target }) =>
                                  onQuestionOptionChangeHandler(
                                    index,
                                    optionIndex,
                                    target.value
                                  )
                                }
                              />
                            </div>
                            <div className="ml-3">
                              {form.values.questions[index].choices[optionIndex]
                                ?.id && values?.id ? (
                                <DeleteSurveyQuestionOptionButton
                                  surveyId={values.id}
                                  id={
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    form.values.questions[index].choices[
                                      optionIndex
                                    ].id!
                                  }
                                  onDone={() =>
                                    onDeleteQuestionOptionHandler(
                                      index,
                                      optionIndex
                                    )
                                  }
                                />
                              ) : (
                                <ActionIcon
                                  onClick={() => {
                                    onDeleteQuestionOptionHandler(
                                      index,
                                      optionIndex
                                    );
                                  }}
                                  variant="transparent"
                                  color="dark"
                                  size="lg"
                                  radius={8}
                                >
                                  <Trash2 size={16} />
                                </ActionIcon>
                              )}
                            </div>
                          </div>
                          {form.values.questions[index].choices[optionIndex]
                            ?.content.length >= maxOptionCharacters && (
                            <Text
                              className="question-options-limit"
                              style={{ color: "#ff0000", fontSize: "12px" }}
                            >
                              You have reached the limit of characters for
                              Option {optionIndex + 1}
                            </Text>
                          )}
                        </>
                      )
                    )}
                  </Stack>
                  <Space h="xl" />
                  <div className="question-options-container">
                    {form.values.questions[index].choices.length >=
                    maxOptions ? (
                      <Text
                        className="question-options-limit"
                        style={{ color: "#ff0000", fontSize: "12px" }}
                      >
                        You have reached the limit of options for this question
                      </Text>
                    ) : (
                      <Button
                        fullWidth
                        styles={{
                          root: {
                            padding: 0,
                          },
                          inner: {
                            float: "left",
                          },
                          label: {
                            color: "#9ca6ae",
                          },
                        }}
                        onClick={() => onAddQuestionOptionHandler(index, "")}
                        leftIcon={<Plus color="#9ca6ae" size={18} />}
                        variant="subtle"
                      >
                        Add an option
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <Space h="xl" />
        </>
      ))}
      <div style={{ paddingTop: "20px" }}>
        <Menu
          styles={{
            root: { width: "100%" },
          }}
          withArrow
          control={
            <Button
              className="Question-Buttons"
              leftIcon={<Plus size={20} className="Question-Add-Button" />}
              variant="subtle"
              color="gray"
            >
              <span className="Add-your-first-question">
                {form.values.questions.length > 0
                  ? "Add a question"
                  : "Add your first question"}
              </span>
            </Button>
          }
        >
          <Menu.Item onClick={() => onAddQuestionHandler("free-text")}>
            Open Text
          </Menu.Item>
          <Menu.Item onClick={() => onAddQuestionHandler("mcq")}>
            Multiple Choice
          </Menu.Item>
        </Menu>
      </div>
    </>
  );
};

export default SurveyQuestionForm;
